import type { SixDigitCode } from '@/types/utils/sixDigitCode.ts';
import { isSpaceKeyEvent } from '@/utils/keyboard.ts';
import { Button } from '@nautilusbio/nb-design-system/Button';
import { getStackClassName } from '@nautilusbio/nb-design-system/Stack';
import Text from '@nautilusbio/nb-design-system/Text';
import { toast } from '@nautilusbio/nb-design-system/Toaster';
import { useCopyToClipboard } from '@nautilusbio/nb-design-system/useCopyToClipboard';
import { useTimeout } from '@nautilusbio/nb-design-system/useTimeout';
import clsx from 'clsx';
import { CopyIcon } from 'lucide-react';
import React, { type FC, type KeyboardEvent, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './styles.module.css';

const COPY_FEEDBACK_DURATION_MS = 1200;

const Code: FC<{ code: SixDigitCode; id: string }> = ({ code, id }) => {
  const { t } = useTranslation('general');
  const { copy } = useCopyToClipboard();
  const { set: scheduleReset } = useTimeout();

  const btnRef = useRef<HTMLButtonElement | null>(null);

  const handleCopy = useCallback(() => {
    // DS copy() is void — errors are caught and logged internally
    copy(code);
    toast.success(`${t('copied')}"${code}"`);

    if (btnRef.current) {
      btnRef.current.dataset.copied = 'true';
      // blur on next tick to ensure any native handling completes
      setTimeout(() => {
        if (btnRef.current) btnRef.current.blur();
      }, 0);

      scheduleReset(() => {
        if (btnRef.current) btnRef.current.dataset.copied = 'false';
      }, COPY_FEEDBACK_DURATION_MS);
    }
  }, [code, copy, scheduleReset, t]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLElement>) => {
      // Accept multiple representations of the Space key across browsers
      if (event.key === 'Enter' || isSpaceKeyEvent(event)) {
        event.preventDefault();
        handleCopy();
      }
    },
    [handleCopy],
  );

  return (
    <Button
      aria-label={`Copy code ${code}`}
      className={clsx(styles.codeWrapper, styles.code, getStackClassName(['displayflex', 'horizontal', 'gap1']))}
      data-testid={`${id}-copy-btn`}
      id={id}
      ref={btnRef}
      onPress={() => handleCopy()}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      visual="unstyled"
    >
      <Text as="span" aria-hidden={false}>
        {t('code')}
      </Text>

      <Text as="strong">{code}</Text>

      <CopyIcon className={styles.copyIcon} aria-hidden size={14} />
    </Button>
  );
};

Code.displayName = 'Code';

export default Code;
